import {Input, ChangeDetectorRef, Component, ChangeDetectionStrategy, OnInit, OnDestroy, NgZone} from '@angular/core';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { takeUntil, tap, isEmpty as _isEmpty, debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

import { IExtendedTab, IFormConfiguration, ISectionConfiguration }
  from '@ispm/therapies/interfaces/section-configuration.interface';
import { get } from 'lodash';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { TreatmentSelectionService } from '@ispm/therapies/services/treatment-selection.service';
import { RESOURCE_TYPE_NAME } from "@ispm/therapies/constants/default-clinical-recommendation-sorting-configuration.constant";
import { ISubjectIdentifier } from "@ispm/therapies/interfaces/subject.interface";
import { TherapiesService } from '@ispm/therapies/services/therapies.service';

const NOT_CALCULATED = -1;

@Component({
  selector: 'gen-treatment-information-wrapper',
  templateUrl: './treatment-information-wrapper.component.html',
  styleUrls: ['./treatment-information-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreatmentInformationWrapperComponent implements OnInit, OnDestroy {
  @Input()
  layout: boolean;

  @Input()
  public section: ISectionConfiguration;

  @Input()
  public navigationPanelConfiguration: IFormConfiguration;

  @Input()
  public subjectIdentifier$: Observable<ISubjectIdentifier>;

  @Input()
  public dataFromLeftPanel$: Observable<any>;

  @Input()
  private isEditable: boolean;

  public widgetUpdateValueCallback: (value) => void;

  public selectedTab: IExtendedTab;
  public isTreatmentSelected: boolean = false;
  public extendedTabs: IExtendedTab[] = [];

  private destroyer: Subject<void> = new Subject<void>();

  public isLoading: boolean = false;

  public therapiesAndTrailCalculatedDetailsHeight$: BehaviorSubject<string>;

  public constructor(
    private therapiesAC: TherapiesActionCreatorService,
    private cd: ChangeDetectorRef,
    public treatmentSelectionService: TreatmentSelectionService,
    private ngZone: NgZone,
    private therapiesService: TherapiesService
  ) {
  }

  public ngOnInit() {
    this.updateConfigurationSelectable();
    this.init();
    this.therapiesAndTrailCalculatedDetailsHeight$ = this.treatmentSelectionService.therapiesAndTrailCalculatedDetailsHeight$;
  }

  public ngOnDestroy(): void {
    this.destroyer.next();
    this.destroyer.complete();
  }

  private tabsInit() {
    this.extendedTabs = this.extendTabs();

    this.ngZone.runOutsideAngular(() => {
      // try to filter resources in extendedTabs when the observables emit values
      combineLatest([
        this.therapiesAC.prefetchTestResources(),
        this.dataFromLeftPanel$,
        this.subjectIdentifier$.pipe(
          tap(() => {
            this.isLoading = true;
            this.cd.markForCheck();
          }),
          debounceTime(200), // due to emitting subjectIdentifier$ at least twice
          // distinctUntilChanged(),
          tap(subjectIdentifier => {
            if (subjectIdentifier.subjectSourceType && this.section.tabPerType) {
              this.tabChanged(subjectIdentifier.subjectSourceType);
            }
          }),
        ),
      ]).pipe(
        tap(([resourcesMap, dataFromLeftPanel, subjectIdentifier]) =>
          this.filterResourcesInTabs(resourcesMap, dataFromLeftPanel, subjectIdentifier)),
        tap(() => this.cd.markForCheck()),
        tap(() => setTimeout(() => {
            this.isLoading = false;
            this.cd.markForCheck();
          }),
        ),
        takeUntil(this.destroyer.asObservable()),
      ).subscribe();
    });
  }

  public panelUpdated() {
    this.extendedTabs = [...this.extendedTabs];
    this.cd.markForCheck();
  }

  private init() {
    // if section tabs are not yet initialized - exit, nothing to do (this method is invoked from several places).
    if (!this.section || !this.section.tabs) {
      return;
    }
    this.tabsInit();

    this.treatmentSelectionService.selection
      .pipe(
        _isEmpty(),
        takeUntil(this.destroyer.asObservable()),
      )
      .subscribe(selectionEmpty => this.isTreatmentSelected = !selectionEmpty);
  }

  private filterResourcesInTabs(resourcesMap: { [name in RESOURCE_TYPE_NAME]?: any}, dataFromLeftPanel: any, subjectIdentifier: ISubjectIdentifier)/*: IExtendedTab[]*/ {
    if (!Object.keys(resourcesMap).length/* || !subjectIdentifier.subjectId*/) { //data is ready and empty
      return [];
    }

    this.extendedTabs.forEach(extendedTab => {
      extendedTab.panels.forEach(extendedPanel => {
        if (extendedPanel.resource && subjectIdentifier.subjectId) {
          if (!this.section.tabPerType && extendedPanel.resource === RESOURCE_TYPE_NAME.TRIALS) {
            extendedPanel.filteredData = this.getSelectedTrials(resourcesMap, subjectIdentifier);
          } else {
            // TODO: fix in DB RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS as displaySingleResource?
            if ((extendedPanel.additionalData?.displaySingleResource
              || extendedPanel.resource === RESOURCE_TYPE_NAME.CLINICAL_TRIAL_RECOMMENDATIONS
            )) {
              extendedPanel.filteredData = resourcesMap[extendedPanel.resource]
                .filter(item => get(item, extendedTab.additionalData?.subjectIdPath || 'id', '') === subjectIdentifier.subjectId);
            } else {
              if (extendedPanel.resource === RESOURCE_TYPE_NAME.THERAPIES) {
                const selectedTherapy = resourcesMap[subjectIdentifier.subjectSourceType]
                  .find(resourceItem => resourceItem.id === subjectIdentifier.subjectId) || { };

                if (extendedPanel.path) {
                  extendedPanel.filteredData = get(selectedTherapy, extendedPanel.path, []);
                } else {
                  extendedPanel.filteredData = [selectedTherapy];
                }
              } else {
                extendedPanel.filteredData = [...resourcesMap[extendedPanel.resource]];
              }
            }
          }
        } else {
          if (Array.isArray(dataFromLeftPanel)) {
            extendedPanel.filteredData = dataFromLeftPanel;
          }
        }
        extendedPanel.amount = extendedPanel.filteredData?.length;
      });
      this.therapiesService.filterTherapiesByCondition(extendedTab);
    });
  }

  private getSelectedTrials(filteredResourcesMap: any, subjectIdentifier: ISubjectIdentifier) {
    if (subjectIdentifier.subjectIdPath) {
      const selectedTherapy = filteredResourcesMap[subjectIdentifier.subjectSourceType]
        .find(therapy => therapy.id === subjectIdentifier.subjectId);

      const selectedContextId = get(selectedTherapy, subjectIdentifier.subjectIdPath, '');
      const selectedContextType = subjectIdentifier.subjectIdPath.split('.')[0].toLowerCase();

      return filteredResourcesMap[RESOURCE_TYPE_NAME.TRIALS].filter(trial =>
        trial.context.find(context =>
          context.resourceType.toLowerCase() === selectedContextType &&
          context.reference === selectedContextId,
        )
      );
    }
    return filteredResourcesMap[RESOURCE_TYPE_NAME.TRIALS];
  }

  private extendTabs(): IExtendedTab[] {
    return this.section.tabs.reduce((accum, tab) => {
      if (tab.panels.length && tab.additionalData?.singlePanel) {
        accum.push({ ...tab,
          name: tab.label,
          amount: NOT_CALCULATED,
          panels: [{
            ...tab.panels[0],
            filteredData: [],
            amount: NOT_CALCULATED,
            openStatus: !!tab.panels[0].opened,
          }]});

      } else {
        accum.push({ ...tab,
          name: tab.label,
          amount: NOT_CALCULATED,
          panels: tab.panels.map(panel => ({
            ...panel,
            filteredData: [],
            amount: NOT_CALCULATED,
            openStatus: !!panel.opened,
          }))});
      }

      return accum;
    }, [] as IExtendedTab[]);
  }

  public updateOpenStatus(status: boolean, index: number, tab: IExtendedTab) {
    const panel = tab.panels.find(tabPanel => tabPanel.opened);

    if (panel) {
      panel.openStatus = false;
      panel.opened = false;
    }
    tab.panels[index].openStatus = status;
    tab.panels[index].opened = status;
  }

  public tabChanged(label: string) {
    if (!label || label === this.selectedTab?.label) {
      return;
    }

    this.selectedTab?.panels.forEach(panel => delete panel.openStatus);
    this.selectedTab = this.extendedTabs.find(tab => tab.label === label);
    this.selectedTab?.panels.forEach(panel => panel.openStatus = panel.opened);

    this.runStateClear();
  }

  private runStateClear() {
    if (this.selectedTab && this.selectedTab.clearOnEnter) {
      this.therapiesAC.initUserReviewState(null, true).then();
    }
  }

  public trackByFn(field?: string) {
    return function (index, item) {
      if (!item) {
        return index;
      }
      return !!field && get(item, field) || index;
    };
  }

  public getItemField(resource: any, path: string) {
    return path ? get(resource, path, null) : resource;
  }

  public updateConfigurationSelectable() {
    this.section.tabs.forEach(tab => {
      tab.panels.forEach(panel => panel['selectAvailable'] = panel.selectable !== false && this.isEditable);
    });
  }

  public tabInit($event: string) {
    if (!this.section.tabPerType || (this.section.tabPerType && this.isEditable)) {
      this.tabChanged($event);
    }
  }

  public isOffPathwayTabSelected(): boolean {
    return this.selectedTab?.label?.startsWith('Off');
  }

  public showTreatmentInformationPanel(): boolean {
    return this.isOffPathwayTabSelected() || this.isTreatmentSelected;
  }
}
